import { useEffect, useState, type ReactNode } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  Activity,
  BarChart2,
  BarChart3,
  ChevronDown,
  HardDrive,
  History,
  KeyRound,
  LogOut,
  Megaphone,
  Network,
  PawPrint,
  Settings,
  Settings2,
  ShieldAlert,
  Users,
  Wallet,
  FileText,
} from 'lucide-react'
import { Spinner, useCurrentUser } from '@dogdisk/shared'
import { NotificationCenter } from './notification-center'
import { AvatarDropdown } from './avatar-dropdown'

type NavLeaf = {
  to: string
  label: string
  icon: typeof Activity
  end?: boolean
}

// 可折叠分组（对齐 .pen 设计稿 3rFRT/FDhzP：域名防墙 父项 + 缩进子项）
type NavGroup = {
  key: string
  label: string
  icon: typeof Activity
  children: NavLeaf[]
}

type NavEntry = NavLeaf | NavGroup

const isGroup = (e: NavEntry): e is NavGroup => 'children' in e

// 顺序与 .pen 设计稿（3rFRT sidebar）一致。
// 2026-05-23 B5 新增 4 入口（登录历史 / 配置历史 / 推荐设置 / 设置）
// 2026-06-17 域名防墙 折叠分组对齐设计稿 FDhzP（密钥配置 / 域名配置 子项）
// 2026-06-29 广告管理分组（小图标 / 瀑布卡片 / 横幅 + 报表）
const NAV: NavEntry[] = [
  { to: '/', label: '仪表盘', icon: Activity, end: true },
  { to: '/stats', label: '数据统计', icon: BarChart3 },
  { to: '/users', label: '用户列表', icon: Users },
  { to: '/transactions', label: '分账流水', icon: FileText },
  { to: '/withdraw', label: '钱包提现', icon: Wallet },
  { to: '/sub-agent-settings', label: '代理设置', icon: Settings2 },
  {
    key: 'domain-guard',
    label: '域名防墙',
    icon: ShieldAlert,
    children: [
      { to: '/domain-guard/keys', label: '密钥配置', icon: KeyRound },
      { to: '/domain-guard/domains', label: '域名配置', icon: Network },
    ],
  },
  {
    key: 'ads',
    label: '广告管理',
    icon: Megaphone,
    children: [
      { to: '/ads/forum-icon', label: '小图标广告位', icon: Activity },
      { to: '/ads/forum-card', label: '瀑布卡片广告位', icon: Activity },
      { to: '/ads/article-banner', label: '横幅广告位', icon: Activity },
      { to: '/ads/report', label: '点击报表', icon: BarChart2 },
    ],
  },
  { to: '/login-history', label: '登录历史', icon: History },
  { to: '/settings', label: '设置', icon: Settings },
]

export type AgentLayoutProps = {
  children: ReactNode
  /** 顶部条标题，默认显示 user + level */
  topbarTitle?: ReactNode
}

export function AgentLayout({ children, topbarTitle }: AgentLayoutProps) {
  const { user, status, signOut } = useCurrentUser()
  const location = useLocation()
  const navigate = useNavigate()

  // 未登录(含登出后)→ 命令式跳 /login。
  // 不用渲染期 <Navigate>:原地 signOut 后 reactive <Navigate> 不真正改 URL(BUG-C11-1)。
  useEffect(() => {
    if (status === 'unauthenticated') {
      navigate(`/login?next=${encodeURIComponent(location.pathname)}`, { replace: true })
    }
  }, [status, location.pathname, navigate])

  // 登出:先 await signOut 再命令式跳 /login(双保险;BUG-C11-1)
  const handleSignOut = async () => {
    await signOut()
    navigate('/login', { replace: true })
  }

  if (status === 'loading' || status === 'unauthenticated') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <Spinner className="text-amber-500" />
      </div>
    )
  }

  if (user && !user.roles.includes('agent')) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-gray-100 text-sm">
        <p className="text-base font-medium text-gray-900">403 — 无代理权限</p>
        <button
          type="button"
          onClick={() => void handleSignOut()}
          className="rounded border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600 hover:bg-gray-50"
        >
          切换账号
        </button>
      </div>
    )
  }

  const headerLabel =
    topbarTitle ?? (user ? `代理 · ${user.username}（一级）` : '代理后台')

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* sidebar — 220px / #1F2937 / 橙色 active */}
      <aside
        className="flex h-screen w-[220px] shrink-0 flex-col gap-1 px-4 py-6"
        style={{ backgroundColor: '#1F2937' }}
      >
        <div className="flex items-center gap-2.5 pb-5">
          <HardDrive size={26} className="text-amber-500" strokeWidth={2.2} />
          <span className="text-[15px] font-bold text-amber-500 tracking-wide">
            DogDisk 代理后台
          </span>
        </div>
        <nav className="flex flex-col gap-0.5">
          {NAV.map((n) =>
            isGroup(n) ? (
              <NavGroupItem key={n.key} group={n} />
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end ?? false}
                className={({ isActive }) =>
                  [
                    'group flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors',
                    isActive
                      ? 'bg-amber-500/10 font-semibold text-amber-500'
                      : 'text-gray-400 hover:bg-white/5 hover:text-gray-200',
                  ].join(' ')
                }
              >
                <n.icon size={18} strokeWidth={2} />
                <span>{n.label}</span>
              </NavLink>
            ),
          )}
        </nav>

        <button
          type="button"
          onClick={() => void handleSignOut()}
          className="mt-auto flex items-center gap-2 rounded-md px-3 py-2.5 text-sm text-gray-400 hover:bg-white/5 hover:text-gray-200"
        >
          <LogOut size={16} strokeWidth={2} />
          <span>退出登录</span>
        </button>
      </aside>

      {/* right column */}
      <div className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden">
        {/* topbar — 56px / 白底 / 下边框 */}
        <header className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-6">
          <div className="text-sm font-medium text-gray-700">
            <PawPrint className="mr-1.5 inline-block text-amber-500" size={14} />
            {headerLabel}
          </div>
          <div className="flex items-center gap-4">
            <NotificationCenter />
            <AvatarDropdown />
          </div>
        </header>

        {/* main scrollable content */}
        <main className="flex-1 overflow-y-auto bg-gray-100">{children}</main>
      </div>
    </div>
  )
}

// 可折叠侧栏分组（对齐设计稿 FDhzP）：父项高亮 + chevron + 缩进子项（左侧指示条）。
// 子路由命中时自动展开；用户可手动折叠/展开。
function NavGroupItem({ group }: { group: NavGroup }) {
  const location = useLocation()
  const childActive = group.children.some((c) =>
    location.pathname.startsWith(c.to),
  )
  const [open, setOpen] = useState(childActive)

  useEffect(() => {
    if (childActive) setOpen(true)
  }, [childActive])

  return (
    <div className="flex flex-col gap-0.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={[
          'flex items-center justify-between gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors',
          childActive
            ? 'font-semibold text-white'
            : 'text-gray-400 hover:bg-white/5 hover:text-gray-200',
        ].join(' ')}
        style={childActive ? { backgroundColor: '#111827' } : undefined}
      >
        <span className="flex items-center gap-2.5">
          <group.icon
            size={18}
            strokeWidth={2}
            className={childActive ? 'text-amber-500' : ''}
          />
          <span>{group.label}</span>
        </span>
        <ChevronDown
          size={14}
          strokeWidth={2}
          className={['transition-transform', open ? '' : '-rotate-90'].join(' ')}
        />
      </button>
      {open && (
        <div className="flex flex-col gap-0.5 pl-4">
          {group.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              className={({ isActive }) =>
                [
                  'group flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] transition-colors',
                  isActive
                    ? 'bg-amber-500/10 font-semibold text-amber-500'
                    : 'text-gray-400 hover:bg-white/5 hover:text-gray-200',
                ].join(' ')
              }
            >
              {({ isActive }) => (
                <>
                  <span
                    className={[
                      'h-3.5 w-[3px] shrink-0 rounded-sm',
                      isActive ? 'bg-amber-500' : 'bg-gray-700',
                    ].join(' ')}
                  />
                  <c.icon size={16} strokeWidth={2} />
                  <span>{c.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  )
}
